import {
  type PageTemplateIdOf,
  resolveStorefrontPageTemplates,
  type StorefrontPage,
  type StorefrontTemplateId,
} from "./storefrontTemplates.js";

/**
 * Pages built from a list of sections: each page's sections by id, with the ids of the variants
 * each is drawn in, the default first. The design system draws them (`HOME_SECTIONS`, held equal
 * to this by a test); the ids live here so a server can check a page's list without React, as it
 * will a store's own. `docs/design-system/templates.md` ("Sections and variants").
 */
export const STOREFRONT_SECTION_VARIANTS = {
  home: {
    hero: ["split", "centered", "carousel", "features"],
    liveHits: ["strip", "cards"],
    recentOpens: ["compact", "cards"],
    featuredBoxes: ["grid", "large", "rail"],
    battles: ["grid", "rows"],
    forge: ["showcase", "band", "stage"],
    promoCards: ["row"],
    products: ["grid"],
    reviews: ["grid"],
    promises: ["grid"],
    faq: ["split", "centered", "stacked"],
    closing: ["band", "breakout"],
  },
  box: {
    heading: ["toolbar"],
    opener: ["stage"],
    volatility: ["meter"],
    recentPulls: ["rail"],
    bestPull: ["strip"],
    odds: ["summary", "bar", "table"],
    drops: ["grid"],
  },
} as const satisfies {
  readonly [P in StorefrontPage]?: Readonly<Record<string, readonly string[]>>;
};

/** A page built from a list of sections. */
export type StorefrontListPage = keyof typeof STOREFRONT_SECTION_VARIANTS;

/** A section of a list page, by id: `"featuredBoxes"`. */
export type StorefrontSectionId<P extends StorefrontListPage> =
  keyof (typeof STOREFRONT_SECTION_VARIANTS)[P] & string;

/**
 * The layouts a page list can be drawn in, each with the slots it places sections in, in order.
 * `column`: `lead`, the top of the page (Home's hero, under a floating nav), then `body`, one
 * column below it. `stage`: `stage`, the top of the page under a spotlight (the box page's name and
 * opener), then three slots of details under it, which phones open in a sheet
 * from the stage instead: `top` and `body` one column each, and `row` side by side between them.
 * A page whose parts sit another way adds a layout of its own, so a stored list always says which
 * slots it means. `split`: `aside`, a column that stays in view beside the details (the box page's
 * box and opener), and beside it `row`, side by side, then `main`, one column; below lg one above
 * the other, and on phones as `stage` draws them.
 */
export const PAGE_LAYOUT_SLOTS = {
  column: ["lead", "body"],
  stage: ["stage", "top", "row", "body"],
  split: ["aside", "row", "main"],
} as const;
export type PageLayout = keyof typeof PAGE_LAYOUT_SLOTS;
export type PageLayoutSlot<L extends PageLayout> = (typeof PAGE_LAYOUT_SLOTS)[L][number];

/**
 * How far apart a page list sets its sections: `roomy` (a showroom's), `tight` (an arcade's).
 * Not the theme's `shape.density`, which sizes controls and padding everywhere.
 */
export const PAGE_SPACINGS = ["roomy", "tight"] as const;
export type PageSpacing = (typeof PAGE_SPACINGS)[number];

/**
 * Who a page is drawn for: a shopper `signedIn`, or a visitor `signedOut`. A list entry naming
 * one is drawn only for it (Home's hero sells the store to a visitor and shows its banners to a
 * shopper); an entry naming none is drawn for both.
 */
export const PAGE_AUDIENCES = ["signedIn", "signedOut"] as const;
export type PageAudience = (typeof PAGE_AUDIENCES)[number];

/** One section on a page: which, the variant it is drawn in, and the settings it changes. */
export interface PageSectionEntry<Id extends string = string> {
  readonly section: Id;
  /** Left out, the section's default (the first of its variants). */
  readonly variant?: string;
  readonly settings?: Readonly<Record<string, unknown>>;
  /** Left out, drawn for every audience. */
  readonly audience?: PageAudience;
}

/**
 * A page as an ordered list of sections in the slots of a layout, at a spacing: the shape a
 * template's preset is written in, and the one a store's own page will be stored in. A section
 * appears at most once for each audience; one it leaves out is not drawn, unless the page
 * requires it (`STOREFRONT_REQUIRED_SECTIONS`, for every audience).
 */
export type PageSectionList<Id extends string = string> = {
  [L in PageLayout]: {
    readonly layout: L;
    readonly spacing: PageSpacing;
    readonly slots: { readonly [S in PageLayoutSlot<L>]: readonly PageSectionEntry<Id>[] };
  };
}[PageLayout];

/**
 * The list as one audience sees it: its entries for that audience or for every audience, in their
 * slots and order. What the design system draws and the dashboard outlines.
 */
export function sectionListFor<List extends PageSectionList>(
  list: List,
  audience: PageAudience,
): List {
  const slots = list.slots as Readonly<Record<string, readonly PageSectionEntry[]>>;
  return {
    ...list,
    slots: Object.fromEntries(
      Object.entries(slots).map(([slot, entries]) => [
        slot,
        entries.filter((entry) => entry.audience === undefined || entry.audience === audience),
      ]),
    ),
  };
}

/**
 * Each Home template as a list of its sections: which it places, in what order and slot, and the
 * variant and settings each is drawn in (its defaults where left out). The shape a store's own
 * Home will be stored in. Ids only: the design system draws them (`HOME_LISTS`, which checks every
 * variant and setting against Home's sections), and the dashboard outlines them.
 */
export const STOREFRONT_HOME_LISTS = {
  /** Catalog first: every section, each in its default variant. */
  showroom: {
    layout: "column",
    spacing: "roomy",
    slots: {
      lead: [{ section: "hero" }],
      body: [
        { section: "liveHits" },
        { section: "recentOpens" },
        { section: "featuredBoxes" },
        { section: "battles" },
        { section: "forge" },
        { section: "promoCards" },
        { section: "products" },
        { section: "reviews" },
        { section: "promises" },
        { section: "faq" },
        { section: "closing" },
      ],
    },
  },
  /** A few boxes up close: the boxes large, the promises straight after, the hero and FAQ centered. */
  spotlight: {
    layout: "column",
    spacing: "roomy",
    slots: {
      lead: [{ section: "hero", variant: "centered" }],
      body: [
        { section: "liveHits" },
        { section: "featuredBoxes", variant: "large" },
        { section: "promises" },
        { section: "recentOpens" },
        { section: "battles" },
        { section: "forge" },
        { section: "promoCards" },
        { section: "reviews" },
        { section: "products" },
        { section: "faq", variant: "centered" },
        { section: "closing" },
      ],
    },
  },
  /**
   * A busy floor of rails, packed closer. Rails and banners lead it for a shopper; a visitor gets
   * the store's games, one at a time, and the closing band's last call to sign up. It leaves out
   * the promo cards (the header's nav already links the games and fairness).
   */
  arcade: {
    layout: "column",
    spacing: "tight",
    slots: {
      lead: [
        { section: "hero", variant: "carousel", settings: { shine: true }, audience: "signedIn" },
        { section: "hero", variant: "features", audience: "signedOut" },
      ],
      body: [
        { section: "featuredBoxes", variant: "rail" },
        { section: "liveHits", variant: "cards" },
        { section: "recentOpens", variant: "cards" },
        { section: "battles", variant: "rows" },
        { section: "forge", variant: "stage" },
        { section: "products" },
        { section: "reviews" },
        { section: "promises" },
        { section: "faq", variant: "stacked" },
        { section: "closing", variant: "breakout", audience: "signedOut" },
      ],
    },
  },
} as const satisfies Record<PageTemplateIdOf<"home">, PageSectionList<StorefrontSectionId<"home">>>;

/**
 * Each box page template as a list of its sections, in `HOME_LISTS`' shape (`BOX_PAGE_LISTS` in
 * the design system draws them).
 */
export const STOREFRONT_BOX_LISTS = {
  /**
   * The box on a lit stage, its controls under it; then recent pulls, the best pull beside the
   * odds, and every item. Phones open the details in a sheet from the stage.
   */
  stage: {
    layout: "stage",
    spacing: "roomy",
    slots: {
      stage: [{ section: "heading" }, { section: "opener" }],
      top: [{ section: "recentPulls" }],
      row: [{ section: "bestPull" }, { section: "odds" }],
      body: [{ section: "drops" }],
    },
  },
  /**
   * The box and its opener in a column that stays in view beside its details: the best pull beside
   * the odds as a bar, recent pulls, and every item four across. Phones as the stage.
   */
  split: {
    layout: "split",
    spacing: "roomy",
    slots: {
      aside: [{ section: "heading" }, { section: "opener" }],
      row: [{ section: "bestPull" }, { section: "odds", variant: "bar" }],
      main: [{ section: "recentPulls" }, { section: "drops", settings: { columns: 4 } }],
    },
  },
} as const satisfies Record<PageTemplateIdOf<"box">, PageSectionList<StorefrontSectionId<"box">>>;

/**
 * The sections a page's every template must draw for every audience, whatever its list: what the
 * page does or must say (its one h1, the opener, the odds), never merchandising. Home needs only
 * its hero; every
 * other section is a preset's (and later a store's) to choose. A page that is not built from a
 * list yet draws every section its content has, so its entry here says what its list will keep.
 */
export const STOREFRONT_REQUIRED_SECTIONS: {
  readonly [P in StorefrontPage]?: readonly string[];
} = {
  home: ["hero"],
  box: ["heading", "opener", "odds"],
};

/**
 * Whether a store's Home leads a visitor with its games (`features`), the hero that never shows
 * banners: so a banner for signed-out visitors shows nowhere on Home. From the store's template
 * and page picks, as the storefront resolves them.
 */
export function homeLeadsVisitorsWithGames(
  template: StorefrontTemplateId,
  pageTemplates: Partial<Record<StorefrontPage, string>> = {},
): boolean {
  const layout = resolveStorefrontPageTemplates(template, pageTemplates).home;
  const list: PageSectionList = sectionListFor(STOREFRONT_HOME_LISTS[layout], "signedOut");
  const entries = Object.values(list.slots).flat();
  return entries.find((entry) => entry.section === "hero")?.variant === "features";
}
